import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

interface CourseListParams {
  page?: number;
  limit?: number;
  level?: string;
}

export function useCourses(params: CourseListParams = {}) {
  const { page = 1, limit = 20, level } = params;

  const queryString = new URLSearchParams();
  queryString.set('page', String(page));
  queryString.set('limit', String(limit));
  if (level) queryString.set('level', level);

  return useQuery({
    queryKey: ['courses', params],
    queryFn: () => api.get<{ courses: any[]; total: number; page: number; limit: number }>(`/courses?${queryString}`),
  });
}

export function useCourse(slug: string) {
  return useQuery({
    queryKey: ['course', slug],
    queryFn: () => api.get<any>(`/courses/${slug}`),
    enabled: !!slug,
  });
}

export function useCourseEnrollmentCheck(slug: string) {
  return useQuery({
    queryKey: ['course-enrollment-check', slug],
    queryFn: () => api.get<{ enrolled: boolean }>(`/courses/${slug}/check`),
    enabled: !!slug,
  });
}

// ---------------------------------------------------------------------------
// Account hooks (authenticated)
// ---------------------------------------------------------------------------

export function useMyEnrollments() {
  return useQuery({
    queryKey: ['my-courses'],
    queryFn: () => api.get<{ enrollments: any[] }>('/my-courses'),
  });
}

export function useCoursePlayer(courseId: string) {
  return useQuery({
    queryKey: ['my-course', courseId],
    queryFn: () => api.get<any>(`/my-courses/${courseId}`),
    enabled: !!courseId,
  });
}

export function useLesson(courseId: string, lessonId: string) {
  return useQuery({
    queryKey: ['my-course-lesson', courseId, lessonId],
    queryFn: () => api.get<any>(`/my-courses/${courseId}/lessons/${lessonId}`),
    enabled: !!courseId && !!lessonId,
  });
}

export function useCourseCertificate(courseId: string) {
  return useQuery({
    queryKey: ['my-course-certificate', courseId],
    queryFn: () => api.get<any>(`/my-courses/${courseId}/certificate`),
    enabled: !!courseId,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useLessonProgressMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, lessonId, progress }: { courseId: string; lessonId: string; progress: any }) =>
      api.post<any>(`/my-courses/${courseId}/lessons/${lessonId}/progress`, progress),
    onSuccess: (_data, { courseId, lessonId }) => {
      queryClient.invalidateQueries({ queryKey: ['my-course', courseId] });
      queryClient.invalidateQueries({ queryKey: ['my-course-lesson', courseId, lessonId] });
    },
  });
}

export function useLessonCompleteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, lessonId }: { courseId: string; lessonId: string }) =>
      api.post<any>(`/my-courses/${courseId}/lessons/${lessonId}/complete`, {}),
    onSuccess: (_data, { courseId, lessonId }) => {
      queryClient.invalidateQueries({ queryKey: ['my-course', courseId] });
      queryClient.invalidateQueries({ queryKey: ['my-course-lesson', courseId, lessonId] });
      queryClient.invalidateQueries({ queryKey: ['my-courses'] });
      queryClient.invalidateQueries({ queryKey: ['my-course-certificate', courseId] });
    },
  });
}

export function useCourseCheckoutMutation() {
  return useMutation({
    mutationFn: (body: { courseId: string; [key: string]: any }) =>
      api.post<any>('/course-checkout', body),
  });
}

export function useSubscribeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { planId: string; [key: string]: any }) =>
      api.post<any>('/course-checkout/subscribe', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['my-courses'] });
    },
  });
}

export function useCancelSubscriptionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<any>('/course-checkout/subscribe/cancel', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Subscription hooks
// ---------------------------------------------------------------------------

export function useSubscriptionStatus() {
  return useQuery({
    queryKey: ['subscription-status'],
    queryFn: () => api.get<any>('/course-checkout/subscribe/status'),
  });
}

// ---------------------------------------------------------------------------
// Pricing hooks
// ---------------------------------------------------------------------------

export function usePricingPlans() {
  return useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => api.get<{ plans: any[] }>('/subscription-plans'),
  });
}
